import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode, type RefObject } from 'react';
import { emailIssue, normalizeWebsite, SlotUnavailableError, type BookingService, type ImplementationEngagement, type Slot } from './booking-service';
import { companyRegistration, COUNTRIES, countryName, defaultCountry, registrationIssue, registrationRule, type CountryCode } from './countries';
import { DOMAIN_MESSAGES } from './domain-messages';
import { defaultLocale, directionOf, intlLocale, LOCALES, MESSAGES, type Locale, type Messages } from './messages';
import { declaredFacts, domainLabels, factDisplay, factLabel, optionLabel, questionHelp, questionLabel, SCOPE_QUESTIONS, type ScopeAnswers } from './scope';
import { addDays, browserTimeZone, civilDate, formatDateTime, formatDay, formatMonth, formatTime, icsInvite, monthGrid, shiftMonth, weekdayLabels, zoneLabel } from './time';

/**
 * Prise de rendez-vous de l'entretien d'implémentation (POC §9A, §10.2 écrans A à C), sur le modèle
 * d'une page de réservation : accueil, choix du jour et de l'heure, coordonnées et quelques
 * questions de cadrage, confirmation puis préparation facultative. Page publique, en huit langues
 * (l'arabe de droite à gauche). Chaque champ est contrôlé pendant la saisie. Les réponses sont des
 * faits **déclarés** : l'entretien les confirmera ou les corrigera.
 */

type Step = 'landing' | 'schedule' | 'details' | 'confirmed';

const TIME_ZONES = ['Europe/Paris', 'Europe/Brussels', 'Europe/Zurich', 'Europe/Luxembourg', 'Europe/London', 'Europe/Berlin', 'Europe/Madrid', 'Europe/Lisbon', 'Africa/Casablanca', 'Africa/Tunis', 'Africa/Algiers', 'Africa/Dakar', 'America/Montreal', 'Indian/Reunion', 'America/Martinique', 'UTC'];

interface Details {
  country: CountryCode;
  legal_name: string;
  registration: string;
  website: string;
  contact_name: string;
  contact_role: string;
  email: string;
  objective: string;
  other_system: string;
  consultant: boolean;
}

type FieldKey = 'legal_name' | 'registration' | 'website' | 'contact_name' | 'email' | 'target_domains';

function browserLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  return navigator.languages !== undefined && navigator.languages.length > 0 ? navigator.languages : [navigator.language];
}

interface PageContext {
  t: Messages;
  locale: Locale;
  /** Étiquette BCP 47 pour Intl. */
  intl: string;
}

export function BookingPage({
  service,
  now = () => new Date(),
  initialTimeZone,
  initialLocale,
  initialCountry,
}: {
  service: BookingService;
  now?: () => Date;
  initialTimeZone?: string;
  initialLocale?: Locale;
  initialCountry?: CountryCode;
}) {
  const [locale, setLocale] = useState<Locale>(() => initialLocale ?? defaultLocale(browserLanguages()));
  const [step, setStep] = useState<Step>('landing');
  const [timeZone, setTimeZone] = useState(() => initialTimeZone ?? browserTimeZone());
  const [slot, setSlot] = useState<Slot | null>(null);
  const [details, setDetails] = useState<Details>(() => ({
    country: initialCountry ?? defaultCountry(browserLanguages()),
    legal_name: '',
    registration: '',
    website: '',
    contact_name: '',
    contact_role: '',
    email: '',
    objective: '',
    other_system: '',
    consultant: false,
  }));
  const [answers, setAnswers] = useState<ScopeAnswers>({});
  const [engagement, setEngagement] = useState<ImplementationEngagement | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const t = MESSAGES[locale];

  useEffect(() => {
    heading.current?.focus();
  }, [step]);

  const ctx: PageContext = { t, locale, intl: intlLocale(locale) };

  let content: ReactNode;
  if (step === 'schedule')
    content = (
      <Scheduler
        ctx={ctx}
        headingRef={heading}
        service={service}
        now={now}
        timeZone={timeZone}
        onTimeZone={(zone) => {
          setTimeZone(zone);
          setSlot(null);
        }}
        selected={slot}
        onBack={() => setStep('landing')}
        onConfirm={(chosen) => {
          setSlot(chosen);
          setStep('details');
        }}
      />
    );
  else if (step === 'details' && slot !== null)
    content = (
      <DetailsForm
        ctx={ctx}
        headingRef={heading}
        service={service}
        slot={slot}
        timeZone={timeZone}
        details={details}
        onDetails={setDetails}
        answers={answers}
        onAnswers={setAnswers}
        onBack={() => setStep('schedule')}
        onSlotLost={() => {
          setSlot(null);
          setStep('schedule');
        }}
        onBooked={(booked) => {
          setEngagement(booked);
          setStep('confirmed');
        }}
      />
    );
  else if (step === 'confirmed' && engagement !== null) content = <Confirmation ctx={ctx} headingRef={heading} engagement={engagement} answers={answers} now={now} service={service} />;
  else content = <Landing ctx={ctx} headingRef={heading} onStart={() => setStep('schedule')} />;

  return (
    <main className="ay-booking" aria-labelledby="ay-booking-title" lang={locale} dir={directionOf(locale)}>
      <div className="ay-booking__topbar">
        <label className="ay-booking__language">
          <span className="ay-field__label">{t.language}</span>
          <select value={locale} onChange={(event) => setLocale(event.target.value as Locale)}>
            {LOCALES.map((entry) => (
              <option key={entry.code} value={entry.code} lang={entry.code}>
                {entry.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="ay-booking__frame">
        {step !== 'landing' && <EventSummary ctx={ctx} event={service.event()} slot={step === 'details' || step === 'confirmed' ? slot : null} timeZone={timeZone} />}
        <section className="ay-booking__content">{content}</section>
      </div>
      <p className="ay-booking__footnote ay-text--muted">{t.demo_footnote}</p>
    </main>
  );
}

/** Flèche « retour » : s'inverse avec le sens d'écriture (CSS). */
function BackArrow() {
  return (
    <span className="ay-booking__flip" aria-hidden="true">
      ←
    </span>
  );
}

function Landing({ ctx, headingRef, onStart }: { ctx: PageContext; headingRef: RefObject<HTMLHeadingElement | null>; onStart: () => void }) {
  const { t } = ctx;
  return (
    <div className="ay-booking__landing">
      <p className="ay-booking__brand">AYUMIA</p>
      <h1 id="ay-booking-title" ref={headingRef} tabIndex={-1}>
        {t.landing_title}
      </h1>
      <p className="ay-booking__lead">{t.landing_lead}</p>
      <ol className="ay-booking__steps">
        <li>
          <strong>{t.landing_step1_title}</strong>
          <span className="ay-text--muted">{t.landing_step1_text}</span>
        </li>
        <li>
          <strong>{t.landing_step2_title}</strong>
          <span className="ay-text--muted">{t.landing_step2_text}</span>
        </li>
        <li>
          <strong>{t.landing_step3_title}</strong>
          <span className="ay-text--muted">{t.landing_step3_text}</span>
        </li>
      </ol>
      <button type="button" className="ay-button ay-booking__cta" onClick={onStart}>
        {t.landing_cta}
      </button>
      <p className="ay-text--muted">{t.landing_note}</p>
    </div>
  );
}

function EventSummary({ ctx, event, slot, timeZone }: { ctx: PageContext; event: ReturnType<BookingService['event']>; slot: Slot | null; timeZone: string }) {
  const { t, intl } = ctx;
  return (
    <aside className="ay-booking__event" aria-label={t.event_label}>
      <p className="ay-booking__brand">AYUMIA</p>
      <h2>{t.event_title}</h2>
      <ul className="ay-booking__facts">
        <li>
          <span className="ay-booking__icon" aria-hidden="true">
            ◷
          </span>{' '}
          {t.minutes(event.duration_minutes)}
        </li>
        <li>
          <span className="ay-booking__icon" aria-hidden="true">
            ▣
          </span>{' '}
          {t.event_channel}
        </li>
        {slot !== null && (
          <li className="ay-booking__chosen">
            <span className="ay-booking__icon" aria-hidden="true">
              ▤
            </span>{' '}
            <bdi>
              {formatDateTime(slot.start, timeZone, intl)} – {formatTime(slot.end, timeZone, intl)}
            </bdi>
          </li>
        )}
        {slot !== null && (
          <li>
            <span className="ay-booking__icon" aria-hidden="true">
              ◎
            </span>{' '}
            <bdi>{timeZone.replace(/_/g, ' ')}</bdi>
          </li>
        )}
      </ul>
      <p className="ay-text--muted">{t.event_description}</p>
    </aside>
  );
}

function Scheduler(props: {
  ctx: PageContext;
  headingRef: RefObject<HTMLHeadingElement | null>;
  service: BookingService;
  now: () => Date;
  timeZone: string;
  onTimeZone: (zone: string) => void;
  selected: Slot | null;
  onBack: () => void;
  onConfirm: (slot: Slot) => void;
}) {
  const { service, now, timeZone, ctx } = props;
  const { t, intl } = ctx;
  const today = civilDate(now(), timeZone);
  const [month, setMonth] = useState(() => (props.selected === null ? today.slice(0, 7) : civilDate(new Date(props.selected.start), timeZone).slice(0, 7)));
  const [state, setState] = useState<{ status: 'loading' } | { status: 'ready'; slots: Slot[] } | { status: 'error' }>({ status: 'loading' });
  const [day, setDay] = useState<string | null>(() => (props.selected === null ? null : civilDate(new Date(props.selected.start), timeZone)));
  const [pending, setPending] = useState<Slot | null>(props.selected);
  const [attempt, setAttempt] = useState(0);
  const grid = useMemo(() => monthGrid(month), [month]);
  const weekdays = useMemo(() => weekdayLabels(intl), [intl]);

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    // Un jour de marge de chaque côté : le fuseau du visiteur peut décaler la date civile d'un créneau.
    const first = addDays(grid[0]![0]!.date, -1);
    const last = addDays(grid[grid.length - 1]![6]!.date, 1);
    service.availability(first, last).then(
      (slots) => active && setState({ status: 'ready', slots }),
      () => active && setState({ status: 'error' }),
    );
    return () => {
      active = false;
    };
  }, [service, grid, attempt]);

  const byDay = useMemo(() => {
    const map = new Map<string, Slot[]>();
    if (state.status !== 'ready') return map;
    for (const slot of state.slots) {
      const date = civilDate(new Date(slot.start), timeZone);
      map.set(date, [...(map.get(date) ?? []), slot]);
    }
    return map;
  }, [state, timeZone]);

  // Premier affichage : si le mois courant n'a plus de créneau, on montre le suivant (une fois).
  const advanced = useRef(false);
  useEffect(() => {
    if (advanced.current || state.status !== 'ready' || props.selected !== null) return;
    advanced.current = true;
    if (![...byDay.keys()].some((date) => date.startsWith(month))) setMonth(shiftMonth(month, 1));
  }, [state, byDay, month, props.selected]);

  const zones = useMemo(() => [...new Set([timeZone, browserTimeZone(), ...TIME_ZONES])], [timeZone]);
  const daySlots = day === null ? [] : (byDay.get(day) ?? []);
  const monthLabel = formatMonth(month, intl);

  return (
    <div className="ay-booking__schedule">
      <div className="ay-booking__header">
        <button type="button" className="ay-icon-button" onClick={props.onBack} aria-label={t.back_home}>
          <BackArrow />
        </button>
        <h1 id="ay-booking-title" ref={props.headingRef} tabIndex={-1}>
          {t.schedule_title}
        </h1>
      </div>
      <div className="ay-booking__picker">
        <div className="ay-booking__calendar">
          <div className="ay-booking__month">
            <button type="button" className="ay-icon-button" onClick={() => setMonth(shiftMonth(month, -1))} disabled={month <= today.slice(0, 7)} aria-label={t.previous_month}>
              <span className="ay-booking__flip" aria-hidden="true">
                ‹
              </span>
            </button>
            <h2 aria-live="polite">{monthLabel}</h2>
            <button type="button" className="ay-icon-button" onClick={() => setMonth(shiftMonth(month, 1))} aria-label={t.next_month}>
              <span className="ay-booking__flip" aria-hidden="true">
                ›
              </span>
            </button>
          </div>
          <table className="ay-booking__grid" aria-label={t.days_of(monthLabel)} aria-busy={state.status === 'loading'}>
            <thead>
              <tr>
                {weekdays.map((label) => (
                  <th key={label} scope="col">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.map((week) => (
                <tr key={week[0]!.date}>
                  {week.map((cell) => {
                    const count = cell.in_month ? (byDay.get(cell.date)?.length ?? 0) : 0;
                    const isSelected = cell.date === day;
                    const label = formatDay(cell.date, intl);
                    return (
                      <td key={cell.date}>
                        {cell.in_month && (
                          <button
                            type="button"
                            className={`ay-booking__day${count > 0 ? ' ay-booking__day--open' : ''}${isSelected ? ' ay-booking__day--selected' : ''}${cell.date === today ? ' ay-booking__day--today' : ''}`}
                            disabled={count === 0}
                            aria-pressed={isSelected}
                            aria-label={count > 0 ? t.day_slots(label, count) : t.day_unavailable(label)}
                            onClick={() => {
                              setDay(cell.date);
                              setPending(null);
                            }}
                          >
                            {cell.day}
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {state.status === 'error' && (
            <div className="ay-banner ay-banner--danger" role="alert">
              {t.availability_error}{' '}
              <button type="button" className="ay-button ay-button--secondary" onClick={() => setAttempt((n) => n + 1)}>
                {t.retry}
              </button>
            </div>
          )}
          {state.status === 'ready' && byDay.size === 0 && <p className="ay-text--muted">{t.no_slot_month}</p>}
          <label className="ay-field ay-booking__zone">
            <span className="ay-field__label">{t.time_zone}</span>
            <select value={timeZone} onChange={(event) => props.onTimeZone(event.target.value)} dir="ltr">
              {zones.map((zone) => (
                <option key={zone} value={zone}>
                  {zoneLabel(zone, now())}
                </option>
              ))}
            </select>
          </label>
        </div>
        {day !== null && (
          <div className="ay-booking__slots" aria-label={t.slots_of(formatDay(day, intl))} role="group">
            <h2>{formatDay(day, intl)}</h2>
            {daySlots.length === 0 && <p className="ay-text--muted">{t.no_slot_day}</p>}
            <ul>
              {daySlots.map((slot) => {
                const chosen = pending?.start === slot.start;
                return (
                  <li key={slot.start} className={chosen ? 'ay-booking__slot ay-booking__slot--chosen' : 'ay-booking__slot'}>
                    <button type="button" className="ay-booking__time" aria-pressed={chosen} onClick={() => setPending(slot)}>
                      {formatTime(slot.start, timeZone, intl)}
                    </button>
                    {chosen && (
                      <button type="button" className="ay-button ay-booking__next" onClick={() => props.onConfirm(slot)}>
                        {t.next}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Contrôle à la saisie : un champ valide affiche une coche dès la frappe ; son erreur s'affiche dès
 * qu'on l'a quitté une fois, puis à chaque frappe. Une erreur de clé de contrôle s'affiche tout de
 * suite : elle n'existe qu'une fois le numéro complet. La soumission ne fait que révéler les
 * erreurs des champs encore jamais visités.
 */
function fieldIssues(details: Details, answers: ScopeAnswers, t: Messages): Partial<Record<FieldKey, { message: string; immediate: boolean }>> {
  const issues: Partial<Record<FieldKey, { message: string; immediate: boolean }>> = {};
  if (details.legal_name.trim() === '') issues.legal_name = { message: t.error_legal_name, immediate: false };
  const registration = registrationIssue(details.country, details.registration);
  if (registration === 'checksum') issues.registration = { message: t.error_registration_checksum, immediate: true };
  else if (registration === 'format') issues.registration = { message: details.country === 'FR' ? t.error_siren_format : t.error_registration_format, immediate: false };
  if (normalizeWebsite(details.website).issue !== null) issues.website = { message: t.error_website, immediate: false };
  if (details.contact_name.trim() === '') issues.contact_name = { message: t.error_contact_name, immediate: false };
  const mail = emailIssue(details.email);
  if (mail !== null) issues.email = { message: mail === 'missing' ? t.error_email_missing : t.error_email_format, immediate: false };
  if ((answers['target_domains'] ?? []).length === 0) issues.target_domains = { message: t.error_domains, immediate: false };
  return issues;
}

function DetailsForm(props: {
  ctx: PageContext;
  headingRef: RefObject<HTMLHeadingElement | null>;
  service: BookingService;
  slot: Slot;
  timeZone: string;
  details: Details;
  onDetails: (details: Details) => void;
  answers: ScopeAnswers;
  onAnswers: (answers: ScopeAnswers) => void;
  onBack: () => void;
  onSlotLost: () => void;
  onBooked: (engagement: ImplementationEngagement) => void;
}) {
  const { details, answers, ctx } = props;
  const { t, locale, intl } = ctx;
  const [touched, setTouched] = useState<ReadonlySet<FieldKey>>(new Set());
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const set = (key: Exclude<keyof Details, 'consultant'>) => (value: string) => props.onDetails({ ...details, [key]: value });
  const touch = (key: FieldKey) => () => setTouched((current) => (current.has(key) ? current : new Set([...current, key])));

  const issues = fieldIssues(details, answers, t);
  const errorOf = (key: FieldKey): string | undefined => {
    const issue = issues[key];
    return issue !== undefined && (submitted || touched.has(key) || issue.immediate) ? issue.message : undefined;
  };
  const validOf = (key: FieldKey, value: string): boolean => issues[key] === undefined && value.trim() !== '';
  const remaining = Object.keys(issues).length;

  const countries = useMemo(() => [...COUNTRIES].map((code) => ({ code, name: countryName(code, locale) })).sort((a, b) => a.name.localeCompare(b.name, intl)), [locale, intl]);
  const rule = registrationRule(details.country);
  const registrationText = DOMAIN_MESSAGES[locale].registration[rule.kind];

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setSubmitted(true);
    setFailure(null);
    if (remaining > 0) {
      // Après le rendu des erreurs : focus sur le premier champ en défaut.
      setTimeout(() => formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(), 0);
      return;
    }
    const systems = [...(answers['current_systems'] ?? [])];
    const other = details.other_system.trim();
    const currentSystems = systems.filter((value) => value !== 'other').concat(systems.includes('other') && other !== '' ? [other] : []);
    const objective = details.objective.trim();
    const company: ImplementationEngagement['company'] = { legal_name: details.legal_name.trim(), country: details.country };
    const registration = companyRegistration(details.country, details.registration);
    if (registration !== undefined) {
      company.registration = registration;
      if (registration.scheme === 'FR_SIRET') {
        company.siren = registration.value.slice(0, 9);
        company.siret = registration.value;
      }
      if (registration.scheme === 'FR_SIREN') company.siren = registration.value;
    }
    const website = normalizeWebsite(details.website).url;
    if (website !== undefined) company.website = website;
    const contact: ImplementationEngagement['primary_contact'] = { name: details.contact_name.trim(), email: details.email.trim() };
    if (details.contact_role.trim() !== '') contact.role = details.contact_role.trim();
    setBusy(true);
    try {
      const booked = await props.service.book({
        slot: props.slot,
        time_zone: props.timeZone,
        locale,
        company,
        primary_contact: contact,
        declared_context: objective === '' ? { current_systems: currentSystems } : { current_systems: currentSystems, primary_objective: objective },
        declared_facts: declaredFacts(answers, objective, locale, other),
        consultant_requested: details.consultant,
      });
      props.onBooked(booked);
    } catch (error) {
      if (error instanceof SlotUnavailableError) {
        setFailure(t.slot_taken);
        setTimeout(props.onSlotLost, 1500);
      } else setFailure(t.booking_failed);
    } finally {
      setBusy(false);
    }
  };

  const toggle = (key: string, value: string, multi: boolean): void => {
    const current = answers[key] ?? [];
    const next = multi ? (current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value]) : current[0] === value ? [] : [value];
    props.onAnswers({ ...answers, [key]: next });
    if (key === 'target_domains') touch('target_domains')();
  };

  return (
    <form ref={formRef} className="ay-booking__form" onSubmit={submit} noValidate>
      <div className="ay-booking__header">
        <button type="button" className="ay-icon-button" onClick={props.onBack} aria-label={t.change_slot}>
          <BackArrow />
        </button>
        <h1 id="ay-booking-title" ref={props.headingRef} tabIndex={-1}>
          {t.details_title}
        </h1>
      </div>

      <fieldset className="ay-booking__section">
        <legend>{t.section_company}</legend>
        <div className="ay-booking__pair">
          <div className="ay-field">
            <label className="ay-field__label" htmlFor="ay-booking-country">
              {t.country}
            </label>
            <select id="ay-booking-country" value={details.country} autoComplete="country" onChange={(event) => set('country')(event.target.value)}>
              {countries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>
          <TextField ctx={ctx} id="legal_name" label={t.legal_name} value={details.legal_name} onChange={set('legal_name')} onBlur={touch('legal_name')} error={errorOf('legal_name')} valid={validOf('legal_name', details.legal_name)} required autoComplete="organization" />
        </div>
        <div className="ay-booking__pair">
          <TextField
            ctx={ctx}
            id="registration"
            label={registrationText.label}
            value={details.registration}
            onChange={set('registration')}
            onBlur={touch('registration')}
            error={errorOf('registration')}
            valid={validOf('registration', details.registration)}
            inputMode={rule.inputMode}
            ltr
            {...(rule.placeholder === undefined ? {} : { placeholder: rule.placeholder })}
            help={registrationText.help}
          />
          <TextField ctx={ctx} id="website" label={t.website} value={details.website} onChange={set('website')} onBlur={touch('website')} error={errorOf('website')} valid={validOf('website', details.website)} inputMode="url" autoComplete="url" ltr help={t.optional} />
        </div>
      </fieldset>

      <fieldset className="ay-booking__section">
        <legend>{t.section_you}</legend>
        <div className="ay-booking__pair">
          <TextField ctx={ctx} id="contact_name" label={t.contact_name} value={details.contact_name} onChange={set('contact_name')} onBlur={touch('contact_name')} error={errorOf('contact_name')} valid={validOf('contact_name', details.contact_name)} required autoComplete="name" />
          <TextField ctx={ctx} id="contact_role" label={t.contact_role} value={details.contact_role} onChange={set('contact_role')} autoComplete="organization-title" help={t.optional} />
        </div>
        <TextField ctx={ctx} id="email" label={t.email} type="email" value={details.email} onChange={set('email')} onBlur={touch('email')} error={errorOf('email')} valid={validOf('email', details.email)} required autoComplete="email" inputMode="email" ltr help={t.email_help} />
      </fieldset>

      <fieldset className="ay-booking__section">
        <legend>{t.section_project}</legend>
        {SCOPE_QUESTIONS.map((question) => {
          const multi = question.kind === 'multi';
          const error = question.key === 'target_domains' ? errorOf('target_domains') : undefined;
          const help = questionHelp(question.key, locale);
          const id = `ay-scope-${question.key}`;
          return (
            <div key={question.key} className="ay-booking__question" role="group" aria-labelledby={`${id}-label`} aria-describedby={error === undefined ? undefined : `${id}-error`}>
              <p id={`${id}-label`} className="ay-booking__question-label">
                {questionLabel(question.key, locale)}
                {question.required === true && <span aria-hidden="true"> *</span>}
              </p>
              {help !== undefined && <p className="ay-field__help">{help}</p>}
              <div className="ay-booking__choices">
                {question.options.map((value) => {
                  const active = (answers[question.key] ?? []).includes(value);
                  return (
                    <button
                      key={value}
                      type="button"
                      className={`ay-booking__choice${active ? ' ay-booking__choice--active' : ''}`}
                      aria-pressed={active}
                      aria-invalid={error !== undefined ? true : undefined}
                      onClick={() => toggle(question.key, value, multi)}
                    >
                      {multi && <span aria-hidden="true">{active ? '✓ ' : '+ '}</span>}
                      <bdi>{optionLabel(question.key, value, locale)}</bdi>
                    </button>
                  );
                })}
              </div>
              {question.key === 'current_systems' && (answers['current_systems'] ?? []).includes('other') && (
                <TextField ctx={ctx} id="other_system" label={t.other_system} value={details.other_system} onChange={set('other_system')} />
              )}
              {error !== undefined && (
                <p id={`${id}-error`} className="ay-field__error">
                  {error}
                </p>
              )}
            </div>
          );
        })}
        <label className="ay-field">
          <span className="ay-field__label">{t.objective}</span>
          <textarea className="ay-booking__textarea" value={details.objective} maxLength={280} rows={3} placeholder={t.objective_placeholder} onChange={(event) => set('objective')(event.target.value)} />
          <span className="ay-field__help">{t.characters_left(280 - details.objective.length)}</span>
        </label>
        <label className="ay-booking__check-option">
          <input type="checkbox" checked={details.consultant} onChange={(event) => props.onDetails({ ...details, consultant: event.target.checked })} />
          <span>
            {t.consultant_option}
            <span className="ay-field__help">{t.consultant_help}</span>
          </span>
        </label>
      </fieldset>

      {submitted && remaining > 0 && (
        <div className="ay-banner ay-banner--danger" role="alert">
          {t.error_form(remaining)}
        </div>
      )}
      {failure !== null && (
        <div className="ay-banner ay-banner--danger" role="alert">
          {failure}
        </div>
      )}
      <div className="ay-booking__actions">
        <button type="submit" className="ay-button ay-booking__cta" disabled={busy}>
          {busy ? t.submitting : t.submit}
        </button>
        <p className="ay-text--muted">{t.privacy_note}</p>
      </div>
    </form>
  );
}

function TextField(props: {
  ctx: PageContext;
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string | undefined;
  valid?: boolean;
  help?: string;
  placeholder?: string;
  required?: boolean;
  type?: string;
  inputMode?: 'numeric' | 'url' | 'email' | 'text';
  autoComplete?: string;
  /** Saisie toujours de gauche à droite (numéros, adresses), même en arabe. */
  ltr?: boolean;
}) {
  const id = `ay-booking-${props.id}`;
  const describedBy = [props.error === undefined ? null : `${id}-error`, props.help === undefined ? null : `${id}-help`].filter((entry) => entry !== null).join(' ');
  const showValid = props.valid === true && props.error === undefined;
  return (
    <div className={`ay-field${showValid ? ' ay-booking__field--valid' : ''}`}>
      <label className="ay-field__label" htmlFor={id}>
        {props.label}
        {props.required === true && (
          <span aria-hidden="true" title={props.ctx.t.required_mark}>
            {' '}
            *
          </span>
        )}
      </label>
      <div className="ay-booking__input">
        <input
          id={id}
          type={props.type ?? 'text'}
          value={props.value}
          required={props.required}
          inputMode={props.inputMode}
          autoComplete={props.autoComplete}
          placeholder={props.placeholder}
          dir={props.ltr === true ? 'ltr' : undefined}
          aria-invalid={props.error === undefined ? undefined : true}
          aria-describedby={describedBy === '' ? undefined : describedBy}
          onChange={(event) => props.onChange(event.target.value)}
          onBlur={props.onBlur}
        />
        {showValid && (
          <span className="ay-booking__valid" role="img" aria-label={props.ctx.t.valid}>
            ✓
          </span>
        )}
      </div>
      {props.error !== undefined && (
        <span id={`${id}-error`} className="ay-field__error">
          {props.error}
        </span>
      )}
      {props.help !== undefined && (
        <span id={`${id}-help`} className="ay-field__help">
          {props.help}
        </span>
      )}
    </div>
  );
}

type PreparationKind = 'file' | 'system' | 'note';

function Confirmation({
  ctx,
  headingRef,
  engagement,
  answers,
  now,
  service,
}: {
  ctx: PageContext;
  headingRef: RefObject<HTMLHeadingElement | null>;
  engagement: ImplementationEngagement;
  answers: ScopeAnswers;
  now: () => Date;
  service: BookingService;
}) {
  const { t, locale, intl } = ctx;
  const [added, setAdded] = useState<Array<{ kind: PreparationKind; label: string }>>([]);
  const [note, setNote] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const zone = engagement.interview.timezone;
  const facts = engagement.pre_discovery.facts;
  const domains = domainLabels(answers, locale);
  const registration = engagement.company.registration;

  const download = (): void => {
    const content = icsInvite({
      uid: `${engagement.engagement_id}@ayumia`,
      start: engagement.interview.scheduled_at,
      end: engagement.interview.ends_at,
      summary: t.ics_summary(engagement.company.legal_name),
      description: t.ics_description,
      stamp: now().toISOString(),
    });
    const url = URL.createObjectURL(new Blob([content], { type: 'text/calendar;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = t.ics_file;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const record = (kind: PreparationKind | 'skipped', label: string): void => {
    void service.addPreparation(engagement.engagement_id, { kind, label });
  };

  const kindLabel = (kind: PreparationKind): string => (kind === 'file' ? t.kind_file : kind === 'system' ? t.kind_system : t.kind_note);

  return (
    <div className="ay-booking__confirmed">
      <p className="ay-booking__check" aria-hidden="true">
        ✓
      </p>
      <h1 id="ay-booking-title" ref={headingRef} tabIndex={-1}>
        {t.confirmed_title}
      </h1>
      <p className="ay-booking__lead">
        <bdi>
          {formatDateTime(engagement.interview.scheduled_at, zone, intl)} – {formatTime(engagement.interview.ends_at, zone, intl)}
        </bdi>{' '}
        (<bdi>{zone.replace(/_/g, ' ')}</bdi>)
      </p>
      <p className="ay-text--muted">{t.confirmation_sent(engagement.primary_contact.email)}</p>
      <div className="ay-stack">
        <button type="button" className="ay-button ay-button--secondary" onClick={download}>
          {t.add_to_calendar}
        </button>
        <a className="ay-button" href="#/entretien">
          {t.start_interview}
        </a>
      </div>

      <section className="ay-booking__section ay-card" aria-labelledby="ay-booking-understood">
        <h2 id="ay-booking-understood">{t.understood_title}</h2>
        <p className="ay-text--muted">{t.understood_lead}</p>
        <dl className="ay-booking__facts-list">
          <div>
            <dt>{t.company}</dt>
            <dd>
              {engagement.company.legal_name} · {countryName(engagement.company.country, locale)}
              {registration !== undefined && (
                <>
                  {' · '}
                  <bdi>{registration.value}</bdi>
                </>
              )}
            </dd>
          </div>
          {facts.map((fact) => (
            <div key={fact.key}>
              <dt>{factLabel(fact, locale) ?? t.objective_fact}</dt>
              <dd>
                {factDisplay(fact, locale, t.list_separator)} <span className="ay-badge">{t.declared}</span>
              </dd>
            </div>
          ))}
        </dl>
        {domains.length > 0 && <p className="ay-text--muted">{t.probable_domains(domains.join(t.list_separator))}</p>}
      </section>

      <section className="ay-booking__section ay-card" aria-labelledby="ay-booking-prepare">
        <h2 id="ay-booking-prepare">{t.prepare_title}</h2>
        <p className="ay-text--muted">{t.prepare_lead}</p>
        {skipped ? (
          <p role="status">{t.prepare_skipped}</p>
        ) : (
          <>
            <div className="ay-booking__prepare">
              <button type="button" className="ay-button ay-button--secondary" onClick={() => {
                  setAdded((list) => [...list, { kind: 'file', label: t.prepare_file_added }]);
                  record('file', t.prepare_file_added);
                }}>
                {t.prepare_file}
              </button>
              <button type="button" className="ay-button ay-button--secondary" onClick={() => {
                  setAdded((list) => [...list, { kind: 'system', label: t.prepare_system_added }]);
                  record('system', t.prepare_system_added);
                }}>
                {t.prepare_system}
              </button>
              <button type="button" className="ay-button ay-button--secondary" onClick={() => setNoteOpen(true)} aria-expanded={noteOpen}>
                {t.prepare_note}
              </button>
              <button type="button" className="ay-button ay-button--secondary" onClick={() => {
                  setSkipped(true);
                  record('skipped', t.prepare_skip);
                }}>
                {t.prepare_skip}
              </button>
            </div>
            {noteOpen && (
              <div className="ay-booking__note">
                <label className="ay-field">
                  <span className="ay-field__label">{t.note_label}</span>
                  <textarea className="ay-booking__textarea" rows={3} maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} />
                </label>
                <button
                  type="button"
                  className="ay-button"
                  disabled={note.trim() === ''}
                  onClick={() => {
                    setAdded((list) => [...list, { kind: 'note', label: note.trim() }]);
                    record('note', note.trim());
                    setNote('');
                    setNoteOpen(false);
                  }}
                >
                  {t.add}
                </button>
              </div>
            )}
            {added.length > 0 && (
              <ul className="ay-booking__added" aria-live="polite">
                {added.map((entry, index) => (
                  <li key={index}>
                    <span className="ay-badge">{kindLabel(entry.kind)}</span> {entry.label}
                  </li>
                ))}
              </ul>
            )}
            <p className="ay-field__help">{t.prepare_demo}</p>
          </>
        )}
      </section>
      {engagement.simulated && <p className="ay-text--muted">{t.demo_reference(engagement.engagement_id)}</p>}
    </div>
  );
}
